import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

/**
 * ClubCombobox — text input + native <datalist> autocomplete backed by the
 * public master-clubs endpoint (`GET /api/clubs/list`).
 *
 * The user can either pick a canonical RI club from the dropdown (which
 * emits both `club_name` and `club_id`) or type a free-text club (which
 * emits `club_id=""`). Falls back gracefully when the master list is
 * empty or the endpoint is unreachable.
 *
 * Props:
 *   - value       : current club_name (string)
 *   - onChange    : (name, club_id) => void
 *   - placeholder : optional placeholder text
 *   - testId      : optional data-testid on the input
 *   - disabled    : bool
 *   - listId      : unique <datalist> id (autogenerated when omitted)
 */
export default function ClubCombobox({
    value,
    onChange,
    placeholder = "Start typing your Rotary club…",
    testId,
    disabled = false,
    listId,
    className,
}) {
    const [clubs, setClubs] = useState([]);
    const rid = useRef(
        listId || `club-list-${Math.random().toString(36).slice(2, 9)}`,
    );

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/clubs/list", { params: { limit: 500 } });
                if (!cancelled) setClubs(data?.clubs || []);
            } catch {
                if (!cancelled) setClubs([]);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const clubsByName = new Map(clubs.map((c) => [c.club_name, c.club_id]));

    const handle = (raw) => {
        const name = String(raw || "");
        const cid = clubsByName.get(name) || "";
        onChange(name, cid);
    };

    return (
        <>
            <Input
                type="text"
                list={rid.current}
                value={value || ""}
                onChange={(e) => handle(e.target.value)}
                placeholder={placeholder}
                data-testid={testId}
                disabled={disabled}
                className={className}
                autoComplete="off"
                spellCheck={false}
            />
            <datalist id={rid.current} data-testid={testId ? `${testId}-datalist` : undefined}>
                {clubs.map((c) => (
                    <option key={c.club_id} value={c.club_name}>
                        {c.club_id ? `RI ${c.club_id}` : ""}
                    </option>
                ))}
            </datalist>
        </>
    );
}
